import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import config from '../../config';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import tokenManager from '../../utils/tokenManager';
import apiClient from '../../utils/axiosInterceptor';
import {
    faCreditCard,
    faSignOutAlt,
    faCrown,
    faShieldAlt,
    faEnvelope,
    faEdit,
    faBell,
    faBellSlash,
} from "@fortawesome/free-solid-svg-icons";
import { User } from '../../types/User';

interface ProfileProps {
    userEmail: string,
    isSubscribed: boolean,
    user: User,
}

const Profile = ({ userEmail, isSubscribed, user }: ProfileProps) => {
    const [newsletterSubscribed, setNewsletterSubscribed] = useState<boolean>(user.newsletterOptIn || false);
    const [isUpdatingNewsletter, setIsUpdatingNewsletter] = useState(false);
    useEffect(() => {
        setNewsletterSubscribed(user.newsletterOptIn || false);
    }, [user.newsletterOptIn]);

    const handleNewsletterToggle = async () => {
        setIsUpdatingNewsletter(true);
        try {
            const newStatus = !newsletterSubscribed;
            const accessToken = await tokenManager.getValidAccessToken();
            if (!accessToken) return;

            await apiClient.put(`${config.backendUrl}users/me`, {
                newsletterOptIn: newStatus
            });
            setNewsletterSubscribed(newStatus);
        } catch {
            setNewsletterSubscribed(!newsletterSubscribed);
        } finally {
            setIsUpdatingNewsletter(false);
        }
    };

    const handleBillingPortal = async () => {
        try {
            const response = await axios.get(`${config.backendUrl}users/customer-portal`, {
                headers: { 'x-auth-token': localStorage.getItem('token') }
            });
            window.open(response.data.url, '_blank');
        } catch {
            /* silent */
        }
    };

    useEffect(() => {
        const migrateNewsletterInfo = async () => {
            if (user.newsletterInfo && typeof user.newsletterInfo === 'object' && Object.keys(user.newsletterInfo).length === 0) {
                try {
                    await axios.post(`${config.backendUrl}users/migrate-newsletter-info`, {}, {
                        headers: { 'x-auth-token': localStorage.getItem('token') }
                    });
                    window.location.reload();
                } catch {
                    /* silent */
                }
            }
        };
        migrateNewsletterInfo();
    }, [user]);

    // Never forced — just surfaced here (and as a lock prompt on Best
    // Matches) as something they can finish whenever they want to. Gated on
    // `topic` specifically, matching the same check the matching algorithm
    // itself requires (server/utils/sponsorMatching.js) and the Best
    // Matches tab lock (Sponsors.tsx) — this used to check `name` instead,
    // which could disagree with those (e.g. a name with no topic would read
    // as "done" here but still be locked out of Best Matches).
    const hasNewsletterProfile = Boolean(user.newsletterInfo?.topic);
    const needsNewsletterSetup = !hasNewsletterProfile;
    const planLabel = isSubscribed
        ? (user.subscription?.toUpperCase() || 'ACTIVE')
        : 'No active plan';

    return (
        <div className="page-shell page-shell--wide profile-page">
                <header className="page-header">
                    <h1 className="page-title">Account</h1>
                    <p className="page-lead">Your subscription and account details.</p>
                </header>

                <section className="panel profile-summary" aria-label="Account overview">
                    <div className="profile-summary__primary">
                        <p className="profile-summary__email">{userEmail || '…'}</p>
                        <span className={`status-pill ${isSubscribed ? 'status-pill--active' : 'status-pill--inactive'}`}>
                            {planLabel}
                        </span>
                    </div>

                    {isSubscribed && user.billing && (
                        <div className="profile-summary__billing">
                            <div className="meta-row">
                                <span className="meta-row__label">Plan</span>
                                <span className="meta-row__value">
                                    ${user.billing.monthlyCharge}/{user.billing.currency?.toUpperCase() || 'USD'} per month
                                </span>
                            </div>
                            {user.billing.nextBillingDate && (
                                <div className="meta-row">
                                    <span className="meta-row__label">Next billing</span>
                                    <span className="meta-row__value">
                                        {new Date(user.billing.nextBillingDate).toLocaleDateString()}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {isSubscribed && (
                        <div className="btn-row profile-summary__actions">
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={handleBillingPortal}
                            >
                                <FontAwesomeIcon icon={faCreditCard} />
                                Manage billing
                            </button>
                        </div>
                    )}
                </section>

                {needsNewsletterSetup && (
                    <section className="panel profile-setup" aria-label="Newsletter profile">
                        <h2 className="panel__title">Improve your matches</h2>
                        <p className="visually-muted profile-setup__text">
                            Add a few details about your newsletter so we can recommend better sponsors.
                        </p>
                        <div className="btn-row">
                            <Link className="btn btn-primary" to="/onboarding">
                                <FontAwesomeIcon icon={faEnvelope} />
                                Complete newsletter profile
                            </Link>
                        </div>
                    </section>
                )}

                {hasNewsletterProfile && (
                    <section className="panel panel--flat" aria-label="Newsletter profile">
                        <h2 className="panel__title">Newsletter profile</h2>
                        {user.newsletterInfo?.name && (
                            <div className="meta-row">
                                <span className="meta-row__label">Name</span>
                                <span className="meta-row__value">{user.newsletterInfo.name}</span>
                            </div>
                        )}
                        {user.newsletterInfo?.topic && (
                            <div className="meta-row">
                                <span className="meta-row__label">Topic</span>
                                <span className="meta-row__value">{user.newsletterInfo.topic}</span>
                            </div>
                        )}
                        {user.newsletterInfo?.audience_size && user.newsletterInfo.audience_size > 0 && (
                            <div className="meta-row">
                                <span className="meta-row__label">Audience</span>
                                <span className="meta-row__value">
                                    {user.newsletterInfo.audience_size.toLocaleString()} subscribers
                                </span>
                            </div>
                        )}
                        <div className="btn-row" style={{ marginTop: 'var(--spacing-md)' }}>
                            <Link to="/onboarding" className="text-link">
                                <FontAwesomeIcon icon={faEdit} /> Update profile
                            </Link>
                        </div>
                    </section>
                )}

                <section className="panel panel--flat profile-settings" aria-label="Settings">
                    <h2 className="panel__title">Settings</h2>

                    <div className="profile-settings__row">
                        <div>
                            <span className="meta-row__value">Weekly email updates</span>
                            <p className="visually-muted profile-settings__hint">
                                Sponsor tips and new opportunities in your inbox.
                            </p>
                        </div>
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={handleNewsletterToggle}
                            disabled={isUpdatingNewsletter}
                        >
                            <FontAwesomeIcon icon={newsletterSubscribed ? faBellSlash : faBell} />
                            {isUpdatingNewsletter
                                ? 'Updating…'
                                : newsletterSubscribed
                                    ? 'Unsubscribe'
                                    : 'Subscribe'}
                        </button>
                    </div>

                    <div className="profile-settings__account-links">
                        <Link to="/change-password" className="text-link">Change password</Link>
                        <button
                            type="button"
                            className="text-link"
                            onClick={async () => {
                                await tokenManager.logout();
                                if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                                    localStorage.setItem('dev_logout', 'true');
                                }
                                window.location.reload();
                            }}
                        >
                            <FontAwesomeIcon icon={faSignOutAlt} /> Sign out
                        </button>
                        {(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
                            <button
                                type="button"
                                className="text-link"
                                onClick={() => {
                                    localStorage.removeItem('dev_logout');
                                    localStorage.setItem('token', 'dev_token_for_localhost');
                                    window.location.reload();
                                }}
                            >
                                <FontAwesomeIcon icon={faCrown} /> Reset dev mode
                            </button>
                        )}
                    </div>
                </section>

                <p className="profile-trust visually-muted">
                    <FontAwesomeIcon icon={faShieldAlt} />
                    Payments are secured by Stripe. We never store card details.
                </p>
        </div>
    );
};

export default Profile;
